import { KakiMakanSectionAndSubSectionsUniqueNames } from "@config/kakiMakan";
import fetchContextWithFallback from "@helper/fetchContextWithFallback";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import { CasBannerWidget } from "@routes/Home/Components/CasBannerWidget";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import KakiMakanRestaurants from "./Components/KakiMakanRestaurants";
import KakiMakanVideoSection from "./Components/KakiMakanVideoSection";
import {
  fetchKakiMakanSectionData,
  KakiMakanPayloadResponseType,
} from "./KakiMakan.server";
import KakiMakanMetaData from "./KakiMakanMetaData";

export function meta() {
  return [getMetaData(KakiMakanMetaData().metaTags)];
}

/**
 * KakiMakanSectionPage
 * Description: [Renders the Kaki Makan landing page with articles and video section]
 * @returns The rendered React element
 */
export default function KakiMakanSectionPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    KakiMakanPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const {
    data: kakiMakanArticles,
    initialRatings,
    kakiMakanBanner,
  } = dataLoaderResponse.payload;

  const SectionName = KakiMakanSectionAndSubSectionsUniqueNames[0];
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    adsSectionName: SectionName,
    pageAdTargetValue: pageAdTargetValue,
    hideNavigationInMobile: true,
  };

  return (
    <BHBasePage {...basePageProps}>
      <WidgetWrapper>
        <div className="flex flex-col gap-4 bg-bg-primary">
          <CasBannerWidget CasBannerWidgetContent={kakiMakanBanner} />
          <div className="flex flex-col gap-4 p-1.5 lg:p-4 items-center">
            <KakiMakanRestaurants
              kakiMakanArticles={kakiMakanArticles}
              initialRatings={initialRatings}
            />
          </div>
          <KakiMakanVideoSection />
        </div>
      </WidgetWrapper>
    </BHBasePage>
  );
}

export async function loader() {
  const data = await fetchKakiMakanSectionData();
  const { gaData } = KakiMakanMetaData();

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  return await fetchContextWithFallback<KakiMakanPayloadResponseType>(args);
}
